import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from "date-fns";
import { BarChart3, CircleDollarSign, CreditCard, ReceiptText, Scissors } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { apiError } from "../api/client";
import { reportesApi } from "../api/reportesApi";
import StatePanel from "../components/StatePanel";
import { formatPYG } from "../utils/currency";

const iso = (date) => format(date, "yyyy-MM-dd");

function ReportCard({ title, rows, valueField = "total" }) {
  const max = Math.max(...(rows || []).map((item) => Number(item[valueField] || 0)), 1);
  return <section className="paper-card p-6">
    <h2 className="text-xl font-black uppercase">{title}</h2>
    <div className="mt-6 space-y-4">{rows?.length ? rows.map((row, index) => {
      const value = Number(row[valueField] || 0);
      return <div key={row.id || index}>
        <div className="mb-1 flex justify-between gap-4 text-sm"><strong>{row.nombre}</strong><span>{formatPYG(value)}</span></div>
        {row.cantidad_ventas != null && <p className="mb-2 text-xs text-zinc-500">{row.cantidad_ventas} venta(s) · ventas por {formatPYG(row.total_ventas)}</p>}
        <div className="h-2 rounded-full bg-zinc-200"><div className="h-full rounded-full bg-barberRed" style={{ width: `${Math.max(3, value / max * 100)}%` }}/></div>
      </div>;
    }) : <p className="text-sm text-zinc-500">Sin datos para el período.</p>}</div>
  </section>;
}

export default function ReportsPage() {
  const today = new Date();
  const [period, setPeriod] = useState("HOY");
  const [range, setRange] = useState({ fecha_desde: iso(today), fecha_hasta: iso(today) });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const choose = (value) => {
    setPeriod(value);
    if (value === "HOY") setRange({ fecha_desde: iso(today), fecha_hasta: iso(today) });
    if (value === "SEMANA") setRange({ fecha_desde: iso(startOfWeek(today, { weekStartsOn: 1 })), fecha_hasta: iso(endOfWeek(today, { weekStartsOn: 1 })) });
    if (value === "MES") setRange({ fecha_desde: iso(startOfMonth(today)), fecha_hasta: iso(endOfMonth(today)) });
  };
  const load = useCallback(() => reportesApi.getSales(range).then(({ data: result }) => { setData(result); setError(""); }).catch((requestError) => setError(requestError.response?.status === 404 ? "Se requiere GET /api/reportes/ventas/?fecha_desde=&fecha_hasta= en el backend." : apiError(requestError))), [range]);
  useEffect(() => { load(); }, [load]);

  const stats = [
    ["Total vendido", formatPYG(data?.total_vendido), CircleDollarSign],
    ["Cantidad de ventas", data?.cantidad_ventas ?? "—", ReceiptText],
    ["Ticket promedio", data ? formatPYG(data.ticket_promedio) : "—", BarChart3],
    ["Comisiones barberos", data ? formatPYG(data.comisiones) : "—", Scissors],
    ["Intereses medios de pago", data ? formatPYG(data.intereses_metodos_pago) : "—", CreditCard],
  ];

  const reports = [
    ["Ventas por método de pago", data?.ventas_por_metodo, "total_ventas"],
    ["Ventas por barbero", data?.ventas_por_barbero, "total_ventas"],
    ["Ventas por servicio", data?.ventas_por_servicio, "total"],
    ["Ventas por producto", data?.ventas_por_producto, "total"],
    ["Comisiones por barbero", data?.comisiones_por_barbero, "comision"],
    ["Intereses por método de pago", data?.intereses_por_metodo, "interes"],
  ];

  return <>
    <p className="eyebrow">Análisis</p><h1 className="display-title mt-2">Reportes</h1><p className="mt-3 text-zinc-600">Ingresos, rendimiento, comisiones e intereses por período.</p>
    <div className="mt-7 flex flex-wrap gap-2">{[["HOY", "Hoy"], ["SEMANA", "Esta semana"], ["MES", "Este mes"], ["PERSONALIZADO", "Personalizado"]].map(([value, label]) => <button key={value} onClick={() => choose(value)} className={`rounded-xl px-5 py-3 text-xs font-black uppercase ${period === value ? "bg-barberRed text-white" : "bg-white text-zinc-600"}`}>{label}</button>)}</div>
    {period === "PERSONALIZADO" && <div className="mt-4 flex max-w-xl gap-3"><input type="date" className="field" value={range.fecha_desde} onChange={(event) => setRange({ ...range, fecha_desde: event.target.value })}/><input type="date" className="field" value={range.fecha_hasta} onChange={(event) => setRange({ ...range, fecha_hasta: event.target.value })}/></div>}
    {error && <div className="mt-5"><StatePanel error={error}/></div>}
    <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">{stats.map(([label, value, Icon]) => <div className="paper-card p-6" key={label}><Icon className="mb-5 text-barberRed"/><p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{label}</p><p className="mt-1 text-3xl font-black">{value}</p></div>)}</div>
    <div className="mt-6 grid gap-5 lg:grid-cols-2">{reports.map(([title, rows, field]) => <ReportCard key={title} title={title} rows={rows} valueField={field}/>)}</div>
  </>;
}
